import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Plus,
  Search,
  ShoppingBag,
  ChevronLeft,
  ChevronRight,
  Users,
  UserRound,
  Link2,
  RefreshCw,
  CheckCircle2,
  XCircle,
  X,
  Package,
  Tag,
  MessageCircle,
  ArrowRightCircle,
  Loader2,
  Filter,
  AlertTriangle,
  CheckSquare,
  UserCheck,
  UserPlus,
  Download,
  Trash2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppLayout from '../layout/AppLayout';
import { orderService, OrderResponse, OrderFilters } from '../services/OrderService';
import chatService from '../services/ChatService';
import { settingsService } from '../services/SettingsService';
import { organizationService, GuestLead, SelectedPart } from '../services/OrganizationService';
import ConfirmModal from '../components/common/modals/ConfirmModal';
import MobileHeader from '../layout/MobileHeader';
import OrderFormModal, { OrderFormData } from '../components/features/orders/OrderFormModal';
import { useOrder } from '../contexts/OrderContext';
import { useSession } from '../contexts/SessionContext';
import { useAuth } from '../contexts/AuthContext';
import { workflowService, WorkflowState } from '../services/WorkflowService';
import OrderRow, { ROW_GRID } from '../components/features/orders/OrderRow';
import { ORDER_SORTS, OrderSort, STAGE_LABEL, ownerTypeOf, partsCounts, sortOrders, stageOf, subjectOf, vehicleOf } from '../components/features/orders/orderLifecycle';
import type { OwnerFilter, StageGroup, OrderAttentionFlag } from '../services/OrderService';


// Plain-language filter options (no query syntax) for the orders search.
const SERVICE_TYPE_OPTIONS = ['maintenance', 'body_damage', 'mechanical', 'other'];
const SOURCE_OPTIONS = ['user', 'guest', 'whatsapp', 'email', 'website', 'api', 'insurance_portal'];
const DATE_PRESET_OPTIONS = ['today', '7d', '30d', 'month'];

/** Turn a friendly date preset into a YYYY-MM-DD created_at lower bound. */
const datePresetToFrom = (preset: string): string | undefined => {
  if (!preset) return undefined;
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (preset === '7d') d.setDate(d.getDate() - 6);          // last 7 days incl. today
  else if (preset === '30d') d.setDate(d.getDate() - 29);   // last 30 days incl. today
  else if (preset === 'month') d.setDate(1);                // since the 1st of this month
  // 'today' → start of today (no adjustment)
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** A removable active-filter chip — shows what's applied so users can clear it. */
const FilterChip: React.FC<{ label: string; value: string; onClear: () => void }> = ({ label, value, onClear }) => (
  <span className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-700 text-xs font-medium rounded-full pl-2.5 pr-1 py-1">
    <span className="text-blue-400">{label}:</span> {value}
    <button type="button" onClick={onClear} className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-blue-200 text-blue-500" aria-label="Remove filter">
      <X className="w-3 h-3" />
    </button>
  </span>
);


const STAGE_GROUPS: StageGroup[] = ['intake', 'review', 'cart', 'ordered', 'cancelled'];
const OWNER_FILTERS: OwnerFilter[] = ['mine', 'unassigned', 'agent'];
const SELECT_CLS = 'h-9 px-2.5 text-[13px] border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-[#3627BF]/30 focus:border-[#3627BF] focus:outline-none';
const INPUT_CLS = SELECT_CLS;
// One key per list row: chat sessions have no order id.
const rowKey = (o: OrderResponse): string => (o.kind === 'session' ? `s:${o.session_id}` : `o:${o.id}`);

/** Stage / needs-action chip in the filter row. */
const Chip: React.FC<{ active: boolean; tone?: 'amber' | 'neutral'; count?: number | null; onClick: () => void; children: React.ReactNode }> = ({ active, tone = 'neutral', count, onClick, children }) => (
  <button
    type="button"
    aria-pressed={active}
    onClick={onClick}
    // A zero-count chip has nothing to show; it stays visible but disabled unless it is the active one.
    disabled={count === 0 && !active}
    className={`inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 text-[13px] font-medium whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-default ${
      tone === 'amber'
        ? (active ? 'border-amber-300 bg-[#FEF3C7] text-[#8A5A00]' : 'border-amber-200 bg-[#FFFBEB] text-[#8A5A00] hover:bg-[#FEF3C7]')
        : (active ? 'border-[#D6D5FF] bg-[#EDEDFF] text-[#3627BF]' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50')
    }`}
  >
    {children}
    {typeof count === 'number' && <span className={`rounded-md px-1.5 text-[12px] font-bold tabular-nums ${active ? 'bg-white/70' : 'bg-gray-100 text-gray-500'}`}>{count}</span>}
  </button>
);

const OrdersManagement: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read tab and page from URL
  const activeTab = (searchParams.get('tab') as 'mine' | 'shared' | 'leads') || 'mine';
  const setActiveTab = (tab: 'mine' | 'shared' | 'leads') => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      next.delete('page');
      return next;
    }, { replace: true });
  };
  const currentPage = Math.max(0, parseInt(searchParams.get('page') || '0', 10));
  const setCurrentPage = (pageOrUpdater: number | ((prev: number) => number)) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      const nextPage = typeof pageOrUpdater === 'function' ? pageOrUpdater(currentPage) : pageOrUpdater;
      if (nextPage === 0) {
        next.delete('page');
      } else {
        next.set('page', String(nextPage));
      }
      return next;
    }, { replace: true });
  };
  // "Show sessions" toggle (Mine tab) — when on, chat sessions that never became
  // orders are merged into the list. Defaults to ON; URL-persisted so refresh/share
  // keeps it (sessions=0 records an explicit opt-out).
  const showSessions = searchParams.get('sessions') !== '0';
  const setShowSessions = (val: boolean) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (val) next.delete('sessions'); else next.set('sessions', '0');
      next.delete('page');
      return next;
    }, { replace: true });
  };
  const { setActiveOrderId, refreshOrders, removeActiveOrderId, setOrderObject } = useOrder();
  const {
    setActiveSessionId,
  } = useSession();
  const { currentUser, isImpersonating, impersonatedUser } = useAuth();
  // When impersonating, use the impersonated user's profile to determine org membership
  const effectiveUser = isImpersonating && impersonatedUser ? impersonatedUser : currentUser;
  const isOrgUser = !!effectiveUser?.organization_id;
  const [orders, setOrders] = useState<OrderResponse[]>([]);
  // Plate-lookup orgs store vin_data.plate; the VIN filter + search match it server-side.
  const hasPlates = orders.some(o => (o.vin_data as any)?.plate);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [takingOrderId, setTakingOrderId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>(() => searchParams.get('status') || '');
  // Lifecycle filters (spec §3, §6): stage group replaces "All Statuses";
  // owner + needs-action ride alongside. Attention comes from the summary chips.
  const [stageGroupFilter, setStageGroupFilter] = useState<StageGroup | ''>(() => (searchParams.get('stage') as StageGroup) || '');
  const [ownerFilter, setOwnerFilter] = useState<OwnerFilter | ''>(() => (searchParams.get('owner') as OwnerFilter) || '');
  const [attentionFilter, setAttentionFilter] = useState<OrderAttentionFlag | ''>(() => (searchParams.get('attention') as OrderAttentionFlag) || '');
  const [needsActionOnly, setNeedsActionOnly] = useState(() => searchParams.get('needs_action') === '1');
  const [searchTerm, setSearchTerm] = useState<string>(() => searchParams.get('q') || '');
  // Simple, non-technical filters (plain dropdowns — no query syntax).
  const [serviceFilter, setServiceFilter] = useState<string>(() => searchParams.get('service') || '');
  const [sourceFilter, setSourceFilter] = useState<string>(() => searchParams.get('source') || '');
  const [makeFilter, setMakeFilter] = useState<string>(() => searchParams.get('make') || '');
  const [vinFilter, setVinFilter] = useState<string>(() => searchParams.get('vin') || '');
  const [customerFilter, setCustomerFilter] = useState<string>(() => searchParams.get('customer') || '');
  const [datePreset, setDatePreset] = useState<string>(() => searchParams.get('date') || '');
  const [showFilters, setShowFilters] = useState(() => ['service', 'source', 'make', 'vin', 'customer', 'date'].some(k => !!searchParams.get(k)));
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(25);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<number | null>(null);
  // Non-order session rows delete via the chat API (string session id).
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Footer sort — reorders the loaded page (the server pages by created_at).
  const [sortKey, setSortKey] = useState<OrderSort>('newest');
  // Rows whose attention strip (questions / reply / log) is open under the row.
  const [expandedRows, setExpandedRows] = useState<Set<number>>(() => new Set());
  // Bulk selection, keyed by rowKey (orders and chat sessions). Shift-click selects a range from the last pick.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const lastPickRef = useRef<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState<string | null>(null);
  const [bulkDelete, setBulkDelete] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [members, setMembers] = useState<{ uid: string; name: string; email: string }[] | null>(null);
  // Live chip counts (per stage group, all, needs_action) returned with every page.
  const [stageCounts, setStageCounts] = useState<Record<string, number> | null>(null);
  const [sharedOrders, setSharedOrders] = useState<OrderResponse[]>([]);
  const [loadingShared, setLoadingShared] = useState(false);
  const [leads, setLeads] = useState<GuestLead[]>([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [leadsSearchTerm, setLeadsSearchTerm] = useState('');
  const [viewingLead, setViewingLead] = useState<GuestLead | null>(null);
  // The org's connected catalogs — power the Make / Model filter dropdown.
  const [availableCatalogs, setAvailableCatalogs] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    settingsService.getUserSettings()
      .then((res) => {
        if (cancelled) return;
        const s = res.data?.settings || {};
        const list: string[] = Array.isArray(s.available_catalogs)
          ? s.available_catalogs
          : (Array.isArray(s.catalogs_config) ? s.catalogs_config.map((c: any) => c?.catalog_name).filter(Boolean) : []);
        // Dedupe case-insensitively so "Kia" and "KIA" collapse to one option
        // (first occurrence wins; the server matches make case-insensitively).
        const seen = new Set<string>();
        const deduped: string[] = [];
        for (const c of list) {
          const v = String(c).trim();
          if (!v || seen.has(v.toLowerCase())) continue;
          seen.add(v.toLowerCase());
          deduped.push(v);
        }
        setAvailableCatalogs(deduped);
      })
      .catch(() => {/* non-fatal — dropdown just falls back to empty */});
    return () => { cancelled = true; };
  }, []);

  // ── Server-side search/filters ───────────────────────────────────────────
  // Search text, make, VIN, source, service, customer and date are ALL applied
  // by the backend across the whole dataset, so results + pagination reflect
  // every record (not just the loaded page). The free-text box is debounced so we
  // don't hit the server on every keystroke; the other filters apply immediately.
  const panelFilterCount = (serviceFilter ? 1 : 0) + (sourceFilter ? 1 : 0) + (makeFilter ? 1 : 0) + (vinFilter.trim() ? 1 : 0) + (customerFilter.trim() ? 1 : 0) + (datePreset ? 1 : 0);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 350);
    return () => clearTimeout(id);
  }, [searchTerm]);
  const filters: OrderFilters = useMemo(() => ({
    search: debouncedSearch || undefined,
    make: makeFilter || undefined,
    vin: vinFilter.trim() || undefined,
    source: sourceFilter || undefined,
    service: serviceFilter || undefined,
    customer: customerFilter.trim() || undefined,
    dateFrom: datePresetToFrom(datePreset),
    stageGroup: stageGroupFilter || undefined,
    owner: ownerFilter || undefined,
    attention: attentionFilter || undefined,
    needsAction: needsActionOnly || undefined,
  }), [debouncedSearch, makeFilter, vinFilter, sourceFilter, serviceFilter, customerFilter, datePreset, stageGroupFilter, ownerFilter, attentionFilter, needsActionOnly]);
  const filterSig = JSON.stringify(filters);

  // Persist every filter in the URL so a refresh, a shared link or the browser
  // back button keeps the same view (tab / page / sessions already live there).
  useEffect(() => {
    const desired: Record<string, string> = {
      q: searchTerm.trim(), status: statusFilter, stage: stageGroupFilter, owner: ownerFilter,
      attention: attentionFilter, needs_action: needsActionOnly ? '1' : '', service: serviceFilter,
      source: sourceFilter, make: makeFilter, vin: vinFilter.trim(), customer: customerFilter.trim(), date: datePreset,
    };
    const changed = Object.entries(desired).some(([k, v]) => (searchParams.get(k) || '') !== v);
    if (!changed) return;
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      Object.entries(desired).forEach(([k, v]) => { if (v) next.set(k, v); else next.delete(k); });
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter, stageGroupFilter, ownerFilter, attentionFilter, needsActionOnly, serviceFilter, sourceFilter, makeFilter, vinFilter, customerFilter, datePreset]);

  useEffect(() => {
    loadOrders();
    removeActiveOrderId();
    setOrderObject(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter, isImpersonating, impersonatedUser, showSessions, filterSig]);

  useEffect(() => {
    if (activeTab === 'shared') loadSharedOrders();
    if (activeTab === 'leads') loadLeads();
  }, [activeTab]);

  const loadSharedOrders = async () => {
    setLoadingShared(true);
    try {
      const resp = await orderService.getOrdersSharedWithMe();
      setSharedOrders(resp.orders);
    } catch (err) {
      console.error('Error loading shared orders:', err);
    } finally {
      setLoadingShared(false);
    }
  };

  const loadLeads = async () => {
    setLoadingLeads(true);
    try {
      const resp = await organizationService.getGuestLeads();
      setLeads(resp.data.leads ?? []);
    } catch (err) {
      console.error('Error loading guest leads:', err);
      setLeads([]);
    } finally {
      setLoadingLeads(false);
    }
  };

  // A lifecycle action returned the refreshed row — splice it in without a reload.
  const handleLifecycleUpdated = (updated: OrderResponse) => {
    setOrders(current => current.map(item => (item.kind !== 'session' && item.id === updated.id) ? updated : item));
  };

  const fetchPage = (offset: number) => isOrgUser
    ? orderService.getOrgOrders(statusFilter || undefined, pageSize, offset, showSessions, filters)
    : orderService.getUserOrders(statusFilter || undefined, pageSize, offset, showSessions, filters);

  const loadOrders = async () => {
    setLoading(true);
    setError(null);
    // Server-side pagination + filtering across ALL records.
    try {
      const response = await fetchPage(currentPage * pageSize);
      setOrders(response.orders);
      setTotalCount(response.total_count);
      setStageCounts(response.stage_counts ?? null);
      setLastRefreshed(new Date());
    } catch (err) {
      setError(t('orders.messages.failedToLoad'));
      console.error('Error loading orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const askDeleteOrder = (orderId: number) => {
    setSessionToDelete(null);
    setOrderToDelete(orderId);
    setShowDeleteConfirm(true);
  };

  const askDeleteSession = (sessionId: string) => {
    setOrderToDelete(null);
    setSessionToDelete(sessionId);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (bulkDelete) {
      setBulkDelete(false);
      await runBulk('delete', (o) => o.kind === 'session' ? chatService.deleteSession(o.session_id!) : orderService.deleteOrder(o.id), selectedRows);
      return;
    }
    if (!orderToDelete && !sessionToDelete) return;
    try {
      if (sessionToDelete) {
        await chatService.deleteSession(sessionToDelete);
      } else if (orderToDelete) {
        await orderService.deleteOrder(orderToDelete);
      }
      await loadOrders();
    } catch (err) {
      setError(t('orders.messages.failedToDelete'));
      console.error('Error deleting:', err);
    } finally {
      setOrderToDelete(null);
      setSessionToDelete(null);
    }
  };

  const openOrder = (order: OrderResponse, isShared = false) => {
    // A non-order session opens its chat directly (no order_id to attach yet).
    if (order.kind === 'session') {
      navigate(`/chat/${order.session_id}`);
      return;
    }
    const url = isShared
      ? `/chat/${order.session_id}?order_id=${order.id}&shared=true`
      : `/chat/${order.session_id}?order_id=${order.id}`;
    navigate(url);
  };

  const handleOpenOrder = (order: OrderResponse, e: React.MouseEvent, isShared = false) => {
    // Don't navigate if clicking on interactive elements (select, button, etc.)
    const target = e.target as HTMLElement;
    if (target.closest('select') || target.closest('button')) return;
    openOrder(order, isShared);
  };

  const toggleStrip = (orderId: number) => setExpandedRows(prev => {
    const next = new Set(prev);
    if (next.has(orderId)) next.delete(orderId); else next.add(orderId);
    return next;
  });

  // Today's delete rule, unchanged: your own session, or an order you personally own.
  const canDelete = (order: OrderResponse): boolean => order.kind === 'session'
    ? order.uid === effectiveUser?.uid
    : order.owner_type === 'person' && order.owner_uid === effectiveUser?.uid;

  const handleTakeOrder = async (order: OrderResponse) => {
    if (!order.id || takingOrderId !== null) return;
    setTakingOrderId(order.id);
    setError(null);
    try {
      const updated = await orderService.lifecycleTake(order.id);
      handleLifecycleUpdated(updated);
    } catch (err) {
      setError(t('orders.messages.failedToTakeOwnership', { defaultValue: 'This order could not be claimed. It may already have an owner.' }));
      await loadOrders();
    } finally {
      setTakingOrderId(null);
    }
  };

  const handleOrderFormSubmit = async (data: OrderFormData, orderId?: number) => {
    setIsSubmitting(true);
    setError(null);
    try {
      if (orderId) {
        // Update existing order
        await orderService.updateOrder(orderId, {
          external_order_id: data.externalOrderId,
          vin: data.vin || null,
          buyer: data.buyer || null,
          service_type: data.serviceType || null
        });
      } else {
        // Create new order
        const newOrder = await orderService.createOrder({
          name: data.externalOrderId,
          transaction_id: orderService.generateTransactionId(),
          external_order_id: data.externalOrderId,
          parts_in_order: {
            items: [],
            total_items: 0,
            export_timestamp: new Date().toISOString(),
            vins: data.vin ? [data.vin] : []
          },
          status: 'draft',
          vin: data.vin || null,
          buyer: data.buyer || null,
          service_type: data.serviceType || null,
          vin_data: data.vinData || null,
          language: data.language || null,
        });

        setActiveSessionId(newOrder.session_id);
        setActiveOrderId(newOrder.id);
        setOrderObject(newOrder);

        // Navigate to chat with the new order
        navigate(`/chat/${newOrder.session_id}?order_id=${newOrder.id}`,
          {
            state: {
              files: data.selectedFiles?.map(file => ({
                data: file.base64,
                mimeType: file.mimeType,
                preview: file.preview,
                name: file.name
              })),
              prompt: "NEXT"
            }
          }
        );
      }
      setShowCreateForm(false);
      await loadOrders();
    } catch (err) {
      setError(orderId
        ? t('orders.messages.failedToUpdate', { defaultValue: 'Failed to update order' })
        : t('orders.messages.failedToCreate', { defaultValue: 'Failed to create order' })
      );
      console.error('Error saving order:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Legacy status chip colours — still used by the "Shared with me" table.
  const getStatusColor = (status: string) => {
    const state = workflowStates.find(s => s.key === status);
    if (state) {
      if (state.is_initial)  return 'bg-amber-50 text-amber-700 border-amber-200';
      if (state.is_terminal) return 'bg-green-50 text-green-700 border-green-200';
      // Cycle through a palette for non-initial, non-terminal states
      const palette = [
        'bg-blue-50 text-blue-700 border-blue-200',
        'bg-indigo-50 text-indigo-700 border-indigo-200',
        'bg-purple-50 text-purple-700 border-purple-200',
        'bg-red-50 text-red-700 border-red-200',
        'bg-orange-50 text-orange-700 border-orange-200',
        'bg-sky-50 text-sky-700 border-sky-200',
      ];
      const idx = workflowStates.filter(s => !s.is_initial && !s.is_terminal).indexOf(state);
      return palette[idx % palette.length];
    }
    // Legacy fallback for known default keys
    switch (status.toLowerCase()) {
      case 'draft':              return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'submitted':          return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'in_progress':        return 'bg-indigo-50 text-indigo-700 border-indigo-200';
      case 'pending_assistance': return 'bg-red-50 text-red-700 border-red-200';
      case 'error':              return 'bg-red-100 text-red-800 border-red-300';
      case 'completed':          return 'bg-green-50 text-green-700 border-green-200';
      case 'cancelled':          return 'bg-gray-100 text-gray-500 border-gray-200';
      default:                   return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getStatusLabel = (status: string) => {
    const state = workflowStates.find(s => s.key === status);
    if (state) return state.label;
    return t(`orders.status.${status}`, { defaultValue: status });
  };

  const [convertingLead, setConvertingLead] = useState<string | null>(null);

  const handleConvertLeadToOrder = async (lead: GuestLead, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!lead.adk_session_id) return;
    setConvertingLead(lead.id);
    try {
      // Use the dedicated backend endpoint — it reads VIN/parts from the ADK session
      // and is idempotent (returns existing order if already converted).
      const { data } = await organizationService.convertLeadToOrder(lead.adk_session_id);
      setViewingLead(null);
      setActiveTab('mine');
      await loadOrders();
      navigate(`/chat/${data.session_id}?order_id=${data.order_id}`);
    } catch (err) {
      console.error('Error converting lead to order:', err);
      setError(t('orders.leads.convertError', { defaultValue: 'Failed to convert lead to order' }));
    } finally {
      setConvertingLead(null);
    }
  };

  // Make / Model dropdown options = the org's available (connected) catalogs, so
  // every catalog is selectable regardless of what's on the current page. Loaded
  // once from user settings. The selected value is sent as the `make` filter and
  // matched server-side against each order's vin_data.datasetName.
  const distinctMakes = useMemo(
    () => [...availableCatalogs].sort((a, b) => a.localeCompare(b)),
    [availableCatalogs],
  );

  // Status has its own inline dropdown on desktop, so it's counted separately
  // from the panel filters (panelFilterCount is defined near the load effect).
  const activeFilterCount = (statusFilter ? 1 : 0) + (stageGroupFilter ? 1 : 0) + (ownerFilter ? 1 : 0) + (attentionFilter ? 1 : 0) + (needsActionOnly ? 1 : 0) + panelFilterCount;
  const clearAllFilters = () => { setStatusFilter(''); setStageGroupFilter(''); setOwnerFilter(''); setAttentionFilter(''); setNeedsActionOnly(false); setServiceFilter(''); setSourceFilter(''); setMakeFilter(''); setVinFilter(''); setCustomerFilter(''); setDatePreset(''); setCurrentPage(0); };

  // The backend already filtered + paginated this page across ALL records; the
  // footer sort only reorders the returned page.
  const pagedOrders = useMemo(() => sortOrders(orders, sortKey), [orders, sortKey]);

  const totalPages = Math.ceil(totalCount / pageSize);

  // ── Bulk selection + actions ─────────────────────────────────────────────
  // Sessions (e.g. invalid-VIN chats) select too, but only delete applies to them.
  const selectedRows = useMemo(() => pagedOrders.filter(o => selectedIds.has(rowKey(o))), [pagedOrders, selectedIds]);
  const selectedOrders = useMemo(() => selectedRows.filter(o => o.kind !== 'session'), [selectedRows]);
  const selecting = selectedIds.size > 0;
  const clearSelection = () => { setSelectedIds(new Set()); lastPickRef.current = null; setAssignOpen(false); };
  const toggleSelect = (order: OrderResponse, shiftKey: boolean) => {
    const key = rowKey(order);
    const idx = pagedOrders.findIndex(o => rowKey(o) === key);
    const from = lastPickRef.current === null ? -1 : pagedOrders.findIndex(o => rowKey(o) === lastPickRef.current);
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (shiftKey && from >= 0 && idx >= 0) {
        const [a, b] = from < idx ? [from, idx] : [idx, from];
        for (let i = a; i <= b; i++) next.add(rowKey(pagedOrders[i]));
        return next;
      }
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    lastPickRef.current = key;
  };
  // A new page or filter set is a new list — the selection does not carry over.
  useEffect(() => { clearSelection(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [currentPage, filterSig, showSessions]);
  // Esc clears the selection (not while typing in a field).
  useEffect(() => {
    if (!selecting) return;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (e.key === 'Escape' && tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') clearSelection();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selecting]);

  // Runs one per-order action over the selection; partial failures are reported, the list reloads either way.
  const runBulk = async (key: string, fn: (o: OrderResponse) => Promise<unknown>, rows: OrderResponse[] = selectedOrders) => {
    setBulkBusy(key); setError(null);
    const results = await Promise.allSettled(rows.map(fn));
    const failed = results.filter(r => r.status === 'rejected').length;
    if (failed) setError(t('orders.bulk.partialFail', { defaultValue: '{{n}} of {{total}} orders could not be updated.', n: failed, total: results.length }));
    await loadOrders();
    clearSelection();
    setBulkBusy(null);
  };
  const bulkTake = () => runBulk('take', (o) => orderService.lifecycleTake(o.id),
    selectedOrders.filter(o => !(o.owner_type === 'person' && o.owner_uid === effectiveUser?.uid)));
  const bulkAssign = (uid: string) => { setAssignOpen(false); return runBulk('assign', (o) => orderService.lifecycleAssign(o.id, uid)); };
  const openAssign = async () => {
    setAssignOpen(v => !v);
    if (members !== null) return;
    try {
      const res = await organizationService.getOrganizationMembers();
      setMembers((res.data?.members || []).filter((m: any) => m.is_active !== false));
    } catch {
      setMembers([]);
      setError(t('orders.bulk.assignUnavailable', { defaultValue: 'Only organisation admins can assign orders.' }));
    }
  };
  const bulkExportCsv = () => {
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const head = ['Order', 'Subject', 'Vehicle', 'VIN', 'Customer', 'Stage', 'Owner', 'Requested', 'Found', 'In cart', 'Updated'];
    const rows = selectedOrders.map(o => {
      const v = vehicleOf(o); const c = partsCounts(o);
      return [o.external_order_id || `#${o.id}`, subjectOf(o) || '', v.model || '', v.vin || '', o.b2b_customer?.name || o.buyer || '',
        STAGE_LABEL[stageOf(o)], o.owner_name || ownerTypeOf(o), c?.requested ?? '', c?.found ?? '', c?.inCart ?? '', o.updated_at];
    });
    const csv = '\ufeff' + [head, ...rows].map(r => r.map(esc).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = `orders-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };
  const allSelectedDeletable = selectedRows.length > 0 && selectedRows.every(canDelete);

  // Keep the current page in range — a filter that shrinks the result set can
  // leave us on an offset past the end; snap back to the first page.
  useEffect(() => {
    if (currentPage > 0 && currentPage >= totalPages) setCurrentPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPages]);

  // Compact found/in-cart indicator for the "Shared with me" table.
  const PartsIndicator: React.FC<{ order: OrderResponse }> = ({ order }) => {
    const counts = partsCounts(order);
    if (!counts) return <span className="text-gray-300" title={t('orders.table.partsNoData', { defaultValue: 'No parts data' })}>—</span>;
    return (
      <span className="text-xs text-[#6B6862] whitespace-nowrap" title={t('orders.table.partsReqFoundInCart', { defaultValue: '{{requested}} requested · {{found}} found · {{inCart}} in cart', ...counts })}>
        {t('orders.table.partsBar', { defaultValue: '{{found}} found · {{inCart}} in cart', found: counts.found, inCart: counts.inCart })}
        {counts.found < counts.requested && <span className="text-gray-400"> / {counts.requested}</span>}
      </span>
    );
  };

  const [workflowStates, setWorkflowStates] = useState<WorkflowState[]>([]);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const autoRefreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const AUTO_REFRESH_INTERVAL_MS = 30_000;

  // Load workflow states once so the shared-orders status chips reflect configured states
  useEffect(() => {
    workflowService.getStateConfig()
      .then(res => setWorkflowStates(res.data.states))
      .catch(() => { /* silently fall back to empty — hardcoded colors handle unknown keys */ });
  }, []);

  // Auto-refresh: poll orders every 30 s while the "mine" tab is active
  const autoRefresh = useCallback(async () => {
    setIsAutoRefreshing(true);
    try {
      const response = await fetchPage(currentPage * pageSize);
      setOrders(response.orders);
      setTotalCount(response.total_count);
      setStageCounts(response.stage_counts ?? null);
      setLastRefreshed(new Date());
    } catch {
      // silently ignore auto-refresh errors
    } finally {
      setIsAutoRefreshing(false);
    }
  }, [isOrgUser, statusFilter, pageSize, currentPage, showSessions, filterSig]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab !== 'mine') {
      if (autoRefreshIntervalRef.current) clearInterval(autoRefreshIntervalRef.current);
      return;
    }
    autoRefreshIntervalRef.current = setInterval(autoRefresh, AUTO_REFRESH_INTERVAL_MS);
    return () => {
      if (autoRefreshIntervalRef.current) clearInterval(autoRefreshIntervalRef.current);
    };
  }, [activeTab, autoRefresh]);

  const setStage = (g: StageGroup | '') => { setStageGroupFilter(g); setCurrentPage(0); };
  const shownFrom = totalCount === 0 ? 0 : currentPage * pageSize + 1;
  const shownTo = Math.min((currentPage + 1) * pageSize, totalCount);

  return (
    <AppLayout>
      <MobileHeader
        title={showCreateForm ? t('orders.actions.createNew', { defaultValue: 'New Order' }) : t('orders.title', { defaultValue: 'Orders' })}
        showBack={true}
        onBackClick={() => showCreateForm ? setShowCreateForm(false) : navigate('/chat')}
      />

      <div className="flex-1 min-h-0 flex flex-col bg-main-bg">
        {/* ── Topbar: title · last sync · refresh · Add Order ── */}
        <div className="hidden sm:flex flex-shrink-0 items-center gap-3 h-[52px] px-4 lg:px-6 bg-white border-b border-gray-200">
          <h1 className="text-[17px] font-semibold text-gray-900 leading-tight">{t('orders.title', { defaultValue: 'Orders Management' })}</h1>
          <div className="ml-auto flex items-center gap-2">
            {activeTab === 'mine' && (
              <>
                {lastRefreshed && (
                  <span className="text-xs text-[#6B6862]" title={lastRefreshed.toLocaleTimeString()}>
                    {t('orders.lastRefreshed', { defaultValue: 'Updated' })} {lastRefreshed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => loadOrders()}
                  disabled={loading || isAutoRefreshing}
                  className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
                  title={t('common.refresh', { defaultValue: 'Refresh' })}
                  aria-label={t('common.refresh', { defaultValue: 'Refresh' })}
                >
                  <RefreshCw className={`w-4 h-4 ${isAutoRefreshing ? 'animate-spin text-[#3627BF]' : 'text-gray-500'}`} />
                </button>
              </>
            )}
            <button type="button" onClick={() => setShowCreateForm(true)} className="btn-primary !h-9 !px-3.5 gap-1.5 text-[13px]">
              <Plus className="w-4 h-4" />
              <span>{t('chat.actions.addOrder', { defaultValue: 'Add Order' })}</span>
            </button>
          </div>
        </div>

        {/* ── Bulk bar: replaces the filter row while rows are selected ── */}
        {activeTab === 'mine' && selecting && (
          <div className="flex-shrink-0 px-3 sm:px-4 lg:px-6 pt-3 pb-2">
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-[#D6D5FF] bg-[#F7F6FF] px-3 py-2">
              <span className="inline-flex items-center gap-2 text-[13px] font-semibold text-gray-900 mr-1">
                <CheckSquare className="w-4 h-4 text-[#3627BF]" />
                {t('orders.bulk.selected', { defaultValue: '{{n}} selected', n: selectedRows.length })}
              </span>
              {selectedOrders.length > 0 && (<>
              <button type="button" onClick={bulkTake} disabled={!!bulkBusy} className="h-8 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-[13px] font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50">
                {bulkBusy === 'take' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserCheck className="w-3.5 h-3.5" />}
                {t('orders.bulk.take', { defaultValue: 'Take orders' })}
              </button>
              {isOrgUser && (
                <div className="relative">
                  <button type="button" onClick={openAssign} disabled={!!bulkBusy} aria-haspopup="menu" aria-expanded={assignOpen} className="h-8 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-[13px] font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50">
                    {bulkBusy === 'assign' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                    {t('orders.bulk.assign', { defaultValue: 'Assign to…' })}
                  </button>
                  {assignOpen && (
                    <div role="menu" className="absolute left-0 top-9 z-20 max-h-64 min-w-[14rem] overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg text-[12px]">
                      {members === null ? (
                        <div className="flex items-center gap-2 px-3 py-2 text-[#6B6862]"><Loader2 className="w-3 h-3 animate-spin" />{t('orders.strip.loading', { defaultValue: 'Loading…' })}</div>
                      ) : members.length === 0 ? (
                        <div className="px-3 py-2 text-[#6B6862]">{t('orders.bulk.noMembers', { defaultValue: 'No members to assign to' })}</div>
                      ) : members.map(m => (
                        <button key={m.uid} type="button" role="menuitem" onClick={() => bulkAssign(m.uid)} className="w-full px-3 py-1.5 text-left hover:bg-gray-50">
                          <div className="text-gray-900">{m.name || m.email}</div>
                          {m.name && <div className="text-[11px] text-[#6B6862]">{m.email}</div>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <button type="button" onClick={bulkExportCsv} disabled={!!bulkBusy} className="h-8 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-[13px] font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50">
                <Download className="w-3.5 h-3.5" />
                {t('orders.bulk.exportCsv', { defaultValue: 'Export CSV' })}
              </button>
              </>)}
              {allSelectedDeletable && (
                <button type="button" onClick={() => { setBulkDelete(true); setOrderToDelete(null); setSessionToDelete(null); setShowDeleteConfirm(true); }} disabled={!!bulkBusy} className="h-8 inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 text-[13px] font-medium text-[#B42318] hover:bg-red-50 disabled:opacity-50">
                  {bulkBusy === 'delete' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  {t('orders.bulk.delete', { defaultValue: 'Delete' })}
                </button>
              )}
              <button type="button" onClick={clearSelection} className="h-8 px-2 text-[13px] font-semibold text-[#3627BF] hover:underline">
                {t('orders.bulk.clear', { defaultValue: 'Clear' })}
              </button>
              <span className="ml-auto hidden sm:inline text-xs text-[#6B6862]">{t('orders.bulk.shiftHint', { defaultValue: 'Shift-click selects a range' })}</span>
            </div>
          </div>
        )}

        {/* ── Filter row: stage chips · search · owner · Filters ── */}
        {activeTab === 'mine' && !selecting && (
          <div className="flex-shrink-0 px-3 sm:px-4 lg:px-6 pt-3 pb-2 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex flex-wrap items-center gap-1.5">
                <Chip tone="amber" active={needsActionOnly} count={stageCounts?.needs_action} onClick={() => { setNeedsActionOnly(v => !v); setCurrentPage(0); }}>
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {t('orders.summary.needsAction', { defaultValue: 'Needs action' })}
                </Chip>
                <Chip active={!stageGroupFilter} count={stageCounts?.all} onClick={() => setStage('')}>{t('orders.filters.all', { defaultValue: 'All' })}</Chip>
                {STAGE_GROUPS.map(g => (
                  <Chip key={g} active={stageGroupFilter === g} count={stageCounts?.[g]} onClick={() => setStage(stageGroupFilter === g ? '' : g)}>
                    {t(`orders.stepper.${g}`, { defaultValue: g })}
                  </Chip>
                ))}
              </div>
              <div className="relative flex-1 min-w-[200px] sm:max-w-md sm:ml-auto">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="search"
                  placeholder={t('orders.filters.searchPlaceholder', { defaultValue: 'Order, VIN, customer or part…' })}
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(0); }}
                  className={`${INPUT_CLS} w-full pl-9 pr-3`}
                  aria-label={t('orders.filters.searchPlaceholder', { defaultValue: 'Search orders' })}
                />
              </div>
              <select
                value={ownerFilter}
                onChange={(e) => { setOwnerFilter(e.target.value as OwnerFilter | ''); setCurrentPage(0); }}
                className={SELECT_CLS}
                aria-label={t('orders.filters.ownerLabel', { defaultValue: 'Owner' })}
              >
                <option value="">{t('orders.filters.anyone', { defaultValue: 'Anyone' })}</option>
                {OWNER_FILTERS.map(o => <option key={o} value={o}>{t(`orders.filters.owner_${o}`, { defaultValue: o === 'mine' ? 'Mine' : o === 'unassigned' ? 'Needs an owner' : 'Agent-driven' })}</option>)}
              </select>
              <button
                type="button"
                onClick={() => setShowFilters(v => !v)}
                aria-expanded={showFilters}
                className={`h-9 px-3 inline-flex items-center gap-2 text-[13px] rounded-lg border transition-colors ${showFilters || panelFilterCount > 0 ? 'bg-[#EDEDFF] border-[#D6D5FF] text-[#3627BF]' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
              >
                <Filter className="w-4 h-4" />
                <span>{t('orders.filters.moreFilters', { defaultValue: 'Filters' })}</span>
                {panelFilterCount > 0 && (
                  <span className="bg-[#3627BF] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{panelFilterCount}</span>
                )}
              </button>
              <button type="button" onClick={() => setShowCreateForm(true)} className="sm:hidden btn-primary !h-9 !px-3 gap-1 text-[13px]" aria-label={t('chat.actions.addOrder', { defaultValue: 'Add Order' })}>
                <Plus className="w-4 h-4" />
              </button>
            </div>

            {/* Secondary filters — plain dropdowns, no syntax to learn */}
            {showFilters && (
              <div className="rounded-xl border border-gray-200 bg-white p-3 flex flex-wrap items-end gap-3">
                <label className="flex flex-col gap-1 text-[11px] font-medium text-gray-500">
                  {t('orders.filters.serviceLabel', { defaultValue: 'Service type' })}
                  <select value={serviceFilter} onChange={(e) => { setServiceFilter(e.target.value); setCurrentPage(0); }} className={`${SELECT_CLS} min-w-[150px] text-gray-800`}>
                    <option value="">{t('orders.filters.allServices', { defaultValue: 'All service types' })}</option>
                    {SERVICE_TYPE_OPTIONS.map(s => (
                      <option key={s} value={s}>{t(`orders.serviceTypes.${s}`, { defaultValue: s.replace(/_/g, ' ') })}</option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-[11px] font-medium text-gray-500">
                  {t('orders.filters.sourceLabel', { defaultValue: 'Source' })}
                  <select value={sourceFilter} onChange={(e) => { setSourceFilter(e.target.value); setCurrentPage(0); }} className={`${SELECT_CLS} min-w-[140px] text-gray-800`}>
                    <option value="">{t('orders.filters.allSources', { defaultValue: 'All sources' })}</option>
                    {SOURCE_OPTIONS.map(s => (
                      <option key={s} value={s}>{t(`orders.sources.${s}`, { defaultValue: s.charAt(0).toUpperCase() + s.slice(1) })}</option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-[11px] font-medium text-gray-500">
                  {t('orders.filters.makeLabel', { defaultValue: 'Make / Model' })}
                  <select value={makeFilter} onChange={(e) => { setMakeFilter(e.target.value); setCurrentPage(0); }} className={`${SELECT_CLS} min-w-[150px] text-gray-800`}>
                    <option value="">{t('orders.filters.allMakes', { defaultValue: 'All makes' })}</option>
                    {distinctMakes.map(m => (
                      <option key={m} value={m}>{t(`models.${m}`, { defaultValue: m })}</option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-[11px] font-medium text-gray-500">
                  {hasPlates ? t('orders.filters.vinPlateLabel', { defaultValue: 'VIN / plate' }) : t('orders.filters.vinLabel', { defaultValue: 'VIN' })}
                  <input type="text" value={vinFilter} onChange={(e) => { setVinFilter(e.target.value); setCurrentPage(0); }} placeholder={t('orders.filters.vinPlaceholder', { defaultValue: 'e.g. KNAPV81…' })} className={`${INPUT_CLS} min-w-[150px] text-gray-800`} />
                </label>
                <label className="flex flex-col gap-1 text-[11px] font-medium text-gray-500">
                  {t('orders.filters.customerLabel', { defaultValue: 'Customer' })}
                  <input type="text" value={customerFilter} onChange={(e) => { setCustomerFilter(e.target.value); setCurrentPage(0); }} placeholder={t('orders.filters.customerPlaceholder', { defaultValue: 'Customer name…' })} className={`${INPUT_CLS} min-w-[150px] text-gray-800`} />
                </label>
                <label className="flex flex-col gap-1 text-[11px] font-medium text-gray-500">
                  {t('orders.filters.dateLabel', { defaultValue: 'Created' })}
                  <select value={datePreset} onChange={(e) => { setDatePreset(e.target.value); setCurrentPage(0); }} className={`${SELECT_CLS} min-w-[140px] text-gray-800`}>
                    <option value="">{t('orders.filters.anyTime', { defaultValue: 'Any time' })}</option>
                    <option value="today">{t('orders.filters.today', { defaultValue: 'Today' })}</option>
                    <option value="7d">{t('orders.filters.last7', { defaultValue: 'Last 7 days' })}</option>
                    <option value="30d">{t('orders.filters.last30', { defaultValue: 'Last 30 days' })}</option>
                    <option value="month">{t('orders.filters.thisMonth', { defaultValue: 'This month' })}</option>
                  </select>
                </label>
                <label
                  className="flex items-center gap-2 h-9 px-3 text-[13px] border border-gray-200 rounded-lg cursor-pointer select-none hover:bg-gray-50"
                  title={t('orders.filters.showSessionsHint', { defaultValue: 'Also show chat sessions that never became orders' })}
                >
                  <input type="checkbox" checked={showSessions} onChange={(e) => setShowSessions(e.target.checked)} className="rounded border-gray-300 text-[#3627BF] focus:ring-[#3627BF]" />
                  <MessageCircle className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600 whitespace-nowrap">{t('orders.filters.showSessions', { defaultValue: 'Show chat sessions' })}</span>
                </label>
                {activeFilterCount > 0 && (
                  <button type="button" onClick={clearAllFilters} className="h-9 px-3 text-[13px] text-gray-500 hover:text-gray-700 underline">
                    {t('orders.filters.clearAll', { defaultValue: 'Clear all' })}
                  </button>
                )}
              </div>
            )}

            {/* Active filter chips — what's applied, each removable */}
            {activeFilterCount > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                {statusFilter && (
                  <FilterChip label={t('orders.filters.chipStatus', { defaultValue: 'Status' })} value={t(`orders.status.${statusFilter}`, { defaultValue: statusFilter.replace(/_/g, ' ') })} onClear={() => { setStatusFilter(''); setCurrentPage(0); }} />
                )}
                {stageGroupFilter && (
                  <FilterChip label={t('orders.filters.stageLabel', { defaultValue: 'Stage' })} value={t(`orders.stepper.${stageGroupFilter}`, { defaultValue: stageGroupFilter })} onClear={() => setStage('')} />
                )}
                {ownerFilter && (
                  <FilterChip label={t('orders.filters.ownerLabel', { defaultValue: 'Owner' })} value={t(`orders.filters.owner_${ownerFilter}`, { defaultValue: ownerFilter })} onClear={() => { setOwnerFilter(''); setCurrentPage(0); }} />
                )}
                {attentionFilter && (
                  <FilterChip label={t('orders.filters.attentionLabel', { defaultValue: 'Attention' })} value={t(`orders.attention.${attentionFilter}`, { defaultValue: attentionFilter.replace(/_/g, ' ') })} onClear={() => { setAttentionFilter(''); setCurrentPage(0); }} />
                )}
                {needsActionOnly && (
                  <FilterChip label={t('orders.filters.attentionLabel', { defaultValue: 'Attention' })} value={t('orders.summary.needsAction', { defaultValue: 'Needs action' })} onClear={() => { setNeedsActionOnly(false); setCurrentPage(0); }} />
                )}
                {serviceFilter && (
                  <FilterChip label={t('orders.filters.serviceLabel', { defaultValue: 'Service type' })} value={t(`orders.serviceTypes.${serviceFilter}`, { defaultValue: serviceFilter.replace(/_/g, ' ') })} onClear={() => setServiceFilter('')} />
                )}
                {sourceFilter && (
                  <FilterChip label={t('orders.filters.sourceLabel', { defaultValue: 'Source' })} value={t(`orders.sources.${sourceFilter}`, { defaultValue: sourceFilter })} onClear={() => setSourceFilter('')} />
                )}
                {makeFilter && (
                  <FilterChip label={t('orders.filters.makeLabel', { defaultValue: 'Make / Model' })} value={t(`models.${makeFilter}`, { defaultValue: makeFilter })} onClear={() => setMakeFilter('')} />
                )}
                {vinFilter.trim() && (
                  <FilterChip label={hasPlates ? t('orders.filters.vinPlateLabel', { defaultValue: 'VIN / plate' }) : t('orders.filters.vinLabel', { defaultValue: 'VIN' })} value={vinFilter.trim()} onClear={() => setVinFilter('')} />
                )}
                {customerFilter.trim() && (
                  <FilterChip label={t('orders.filters.customerLabel', { defaultValue: 'Customer' })} value={customerFilter.trim()} onClear={() => setCustomerFilter('')} />
                )}
                {datePreset && (
                  <FilterChip label={t('orders.filters.dateLabel', { defaultValue: 'Created' })} value={t(`orders.filters.${datePreset === '7d' ? 'last7' : datePreset === '30d' ? 'last30' : datePreset === 'month' ? 'thisMonth' : 'today'}`, { defaultValue: datePreset })} onClear={() => setDatePreset('')} />
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Scrolling body: the topbar, filter row and footer stay put ── */}
        <div className="flex-1 min-h-0 overflow-auto px-3 sm:px-4 lg:px-6 pb-3 pt-1">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* ── SHARED WITH ME TAB ── */}
          {activeTab === 'shared' && (
            <>
              {loadingShared ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
              ) : sharedOrders.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col items-center justify-center py-16 text-gray-500">
                  <Users className="w-12 h-12 mb-3 text-gray-300" />
                  <p className="font-medium">{t('orders.share.noSharedOrders', { defaultValue: 'No shared orders yet' })}</p>
                  <p className="text-sm text-gray-400 mt-1">{t('orders.share.noSharedOrdersHint', { defaultValue: 'Orders shared with you will appear here once you accept an invitation' })}</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium text-gray-600 whitespace-nowrap">{t('orders.table.id', { defaultValue: 'Id' })}</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-600 whitespace-nowrap">{t('orders.table.status', { defaultValue: 'Status' })}</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-600 hidden lg:table-cell whitespace-nowrap">{t('orders.table.make', { defaultValue: 'Make' })}</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-600 hidden lg:table-cell whitespace-nowrap">{t('orders.table.VIN', { defaultValue: 'VIN' })}</th>
                          <th className="px-4 py-3 text-center font-medium text-gray-600 hidden sm:table-cell whitespace-nowrap">{t('orders.table.reqFoundInCart', { defaultValue: 'Parts (req/found/incart)' })}</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-600 hidden sm:table-cell whitespace-nowrap">{t('orders.table.lastUpdated', { defaultValue: 'Last Updated' })}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {sharedOrders.map(order => (
                          <tr
                            key={order.id}
                            onClick={(e) => handleOpenOrder(order, e, true)}
                            className="hover:bg-gray-50 cursor-pointer transition-colors"
                          >
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900 truncate">{order.external_order_id || `#${order.id}`}</div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`text-xs font-medium rounded-full px-3 py-1 border ${getStatusColor(order.status)}`}>
                                {getStatusLabel(order.status)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-600 hidden lg:table-cell">
                              <span className="truncate block">{order.vin_data?.datasetName ? t(`models.${order.vin_data.datasetName}`, { defaultValue: order.vin_data.datasetName }) : '-'}</span>
                            </td>
                            <td className="px-4 py-3 text-gray-600 font-mono text-xs hidden lg:table-cell">
                              <span className="truncate block" title={order.vin_data?.plate && order.vin ? `VIN ${order.vin}` : undefined}>{order.vin_data?.plate || order.vin || '-'}</span>
                            </td>
                            <td className="px-4 py-3 text-center text-gray-600 hidden sm:table-cell"><PartsIndicator order={order} /></td>
                            <td className="px-4 py-3 text-gray-500 hidden sm:table-cell">{new Date(order.updated_at).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── LEADS TAB ── */}
          {activeTab === 'leads' && (
            <div className="space-y-4">
              {/* Header */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                <div>
                  <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                    <UserRound className="w-5 h-5 text-amber-500" />
                    {t('orders.leads.title', { defaultValue: 'Leads' })}
                  </h2>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder={t('orders.leads.searchPlaceholder', { defaultValue: 'Search leads…' })}
                      value={leadsSearchTerm}
                      onChange={e => setLeadsSearchTerm(e.target.value)}
                      className="h-9 pl-9 pr-4 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-52"
                    />
                  </div>
                  <button
                    onClick={loadLeads}
                    disabled={loadingLeads}
                    className="h-9 px-3 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1.5"
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingLeads ? 'animate-spin' : ''}`} />
                    {t('common.refresh', { defaultValue: 'Refresh' })}
                  </button>
                </div>
              </div>

              {loadingLeads ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
                </div>
              ) : leads.filter(l => l.parts_count > 0).length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col items-center justify-center py-16 text-gray-500">
                  <Package className="w-12 h-12 mb-3 text-gray-300" />
                  <p className="font-medium">{t('orders.leads.noLeads', { defaultValue: 'No leads with parts yet' })}</p>
                  <p className="text-sm text-gray-400 mt-1">
                    {t('orders.leads.noLeadsHint', { defaultValue: 'Leads will appear here once a visitor finds parts via a guest invite link' })}
                  </p>
                </div>
              ) : (() => {
                const filtered = leads.filter(lead => {
                  // Only show leads that have parts
                  if (!lead.has_session || lead.parts_count === 0) return false;
                  if (!leadsSearchTerm) return true;
                  const q = leadsSearchTerm.toLowerCase();
                  return (
                    lead.invite_label.toLowerCase().includes(q) ||
                    (lead.guest_uid?.toLowerCase().includes(q) ?? false) ||
                    (lead.visitor_name?.toLowerCase().includes(q) ?? false) ||
                    (lead.visitor_email?.toLowerCase().includes(q) ?? false) ||
                    (lead.visitor_phone?.toLowerCase().includes(q) ?? false) ||
                    lead.invite_token.toLowerCase().includes(q)
                  );
                });
                return (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    {/* Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-4 py-3 text-left font-medium text-gray-600 whitespace-nowrap">
                              {t('orders.leads.table.lead', { defaultValue: 'Visitor' })}
                            </th>
                            <th className="px-4 py-3 text-left font-medium text-gray-600 whitespace-nowrap hidden sm:table-cell">
                              {t('orders.leads.table.source', { defaultValue: 'Source Link' })}
                            </th>
                            <th className="px-4 py-3 text-center font-medium text-gray-600 whitespace-nowrap">
                              {t('orders.leads.table.parts', { defaultValue: 'Parts' })}
                            </th>
                            <th className="px-4 py-3 text-center font-medium text-gray-600 whitespace-nowrap hidden md:table-cell">
                              {t('orders.leads.table.linkStatus', { defaultValue: 'Link' })}
                            </th>
                            <th className="px-4 py-3 text-left font-medium text-gray-600 whitespace-nowrap">
                              {t('orders.leads.table.date', { defaultValue: 'Date' })}
                            </th>
                            <th className="px-4 py-3 text-right font-medium text-gray-600 whitespace-nowrap">
                              {t('orders.leads.table.actions', { defaultValue: 'Actions' })}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {filtered.map(lead => (
                            <tr
                              key={lead.id}
                              className="hover:bg-amber-50 cursor-pointer transition-colors"
                              onClick={() => setViewingLead(lead)}
                            >
                              {/* Visitor identity */}
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                                    <UserRound className="w-4 h-4 text-amber-600" />
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-medium text-gray-900 truncate">
                                      {lead.visitor_name || lead.visitor_email || lead.visitor_phone || `Visitor #${lead.guest_uid?.slice(-6) ?? '?'}`}
                                    </p>
                                    <p className="text-xs text-gray-400 truncate">
                                      {lead.visitor_phone
                                        ? lead.visitor_phone
                                        : lead.visitor_email
                                        ? lead.visitor_email
                                        : lead.language
                                        ? lead.language.toUpperCase()
                                        : ''}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              {/* Source invite */}
                              <td className="px-4 py-3 hidden sm:table-cell">
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <Link2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                  <span className="text-gray-700 truncate">{lead.invite_label}</span>
                                </div>
                                <p className="text-xs text-gray-400 font-mono mt-0.5 truncate">
                                  {lead.invite_token.slice(0, 16)}…
                                </p>
                              </td>
                              {/* Parts count */}
                              <td className="px-4 py-3 text-center">
                                <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                                  <Package className="w-3 h-3" />
                                  {lead.parts_count}
                                </span>
                              </td>
                              {/* Link active/revoked */}
                              <td className="px-4 py-3 text-center hidden md:table-cell">
                                {lead.is_invite_active ? (
                                  <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                                    <CheckCircle2 className="w-3 h-3" />
                                    {t('orders.leads.linkActive', { defaultValue: 'Active' })}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-xs text-red-600 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                                    <XCircle className="w-3 h-3" />
                                    {t('orders.leads.linkRevoked', { defaultValue: 'Revoked' })}
                                  </span>
                                )}
                              </td>
                              {/* Date */}
                              <td className="px-4 py-3 text-gray-500 text-xs">
                                {new Date(lead.created_at).toLocaleDateString()}
                              </td>
                              {/* Actions */}
                              <td className="px-4 py-3 text-right">
                                <button
                                  onClick={(e) => handleConvertLeadToOrder(lead, e)}
                                  disabled={convertingLead === lead.id}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors disabled:opacity-50"
                                  title={t('orders.leads.convertToOrder', { defaultValue: 'Convert to Order' })}
                                >
                                  {convertingLead === lead.id
                                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    : <ArrowRightCircle className="w-3.5 h-3.5" />}
                                  {t('orders.leads.convert', { defaultValue: 'Convert' })}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {filtered.length === 0 && (
                      <div className="text-center py-8 text-gray-500 text-sm">
                        {leadsSearchTerm
                          ? t('orders.leads.noMatchingLeads', { defaultValue: 'No leads match your search' })
                          : t('orders.leads.noLeadsWithParts', { defaultValue: 'No leads with parts found yet' })}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* ── MY ORDERS TAB ── */}
          {activeTab === 'mine' && (
            loading ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3627BF]" />
              </div>
            ) : pagedOrders.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col items-center justify-center py-16 text-gray-500">
                <ShoppingBag className="w-12 h-12 mb-3 text-gray-300" />
                <p className="font-medium">{t('orders.messages.noOrdersFound', { defaultValue: 'No orders found' })}</p>
                <p className="text-sm text-gray-400 mt-1">
                  {searchTerm || activeFilterCount > 0
                    ? t('orders.messages.adjustSearchCriteria', { defaultValue: 'Try adjusting your search' })
                    : t('orders.messages.noOrdersYet', { defaultValue: 'Create your first order to get started' })
                  }
                </p>
                {activeFilterCount > 0 && (
                  <button type="button" onClick={clearAllFilters} className="mt-3 text-sm text-[#3627BF] underline">
                    {t('orders.filters.clearAll', { defaultValue: 'Clear all' })}
                  </button>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className={`${ROW_GRID} hidden md:grid sticky top-0 z-10 h-[34px] rounded-t-xl border-b border-gray-200 bg-[#FAF9F6] text-[10px] font-bold uppercase tracking-wider text-[#6B6862]`}>
                  <span>{t('orders.table.order', { defaultValue: 'Order' })}</span>
                  <span>{t('orders.table.vehicle', { defaultValue: 'Vehicle' })}</span>
                  <span className="hidden xl:block">{t('orders.table.customer', { defaultValue: 'Customer' })}</span>
                  <span className="hidden lg:block">{t('orders.table.parts', { defaultValue: 'Parts' })}</span>
                  <span>{t('orders.table.stageTime', { defaultValue: 'Stage · time' })}</span>
                  <span>{t('orders.table.owner', { defaultValue: 'Owner' })}</span>
                  <span>{t('orders.table.nextStep', { defaultValue: 'Next step' })}</span>
                  <span />
                </div>
                {pagedOrders.map((order) => (
                  <OrderRow
                    key={rowKey(order)}
                    order={order}
                    currentUid={effectiveUser?.uid}
                    canDelete={canDelete(order)}
                    expanded={order.kind !== 'session' && expandedRows.has(order.id)}
                    taking={takingOrderId === order.id}
                    selected={selectedIds.has(rowKey(order))}
                    selecting={selecting}
                    onSelect={(shift) => toggleSelect(order, shift)}
                    onOpen={() => openOrder(order)}
                    onToggleStrip={() => toggleStrip(order.id)}
                    onTake={() => handleTakeOrder(order)}
                    onDelete={() => order.kind === 'session' ? askDeleteSession(order.session_id!) : askDeleteOrder(order.id!)}
                    onUpdated={handleLifecycleUpdated}
                  />
                ))}
              </div>
            )
          )}
        </div>

        {/* ── Footer: count · sort · pagination ── */}
        {activeTab === 'mine' && (
          <div className="flex-shrink-0 flex flex-wrap items-center gap-x-4 gap-y-1 px-3 sm:px-4 lg:px-6 py-2 bg-white border-t border-gray-200 text-xs text-[#6B6862]">
            <span>
              <span className="font-semibold text-gray-900 tabular-nums">{shownFrom}–{shownTo}</span> {t('orders.pagination.of', { defaultValue: 'of' })} <span className="tabular-nums">{totalCount}</span> {t('orders.pagination.orders', { defaultValue: 'orders' })}
            </span>
            <label className="inline-flex items-center gap-1.5">
              {t('orders.sort.label', { defaultValue: 'Sort' })}
              <select value={sortKey} onChange={(e) => setSortKey(e.target.value as OrderSort)} className="h-7 rounded-md border border-gray-200 bg-white px-1.5 text-xs font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#3627BF]/30">
                {ORDER_SORTS.map(k => (
                  <option key={k} value={k}>
                    {t(`orders.sort.${k}`, { defaultValue: k === 'newest' ? 'Newest first' : k === 'needs_action' ? 'Needs action first' : k === 'sla' ? 'Next SLA due' : k === 'waiting' ? 'Waiting longest' : 'Recently updated' })}
                  </option>
                ))}
              </select>
            </label>
            <div className="ml-auto inline-flex items-center gap-1">
              <span className="mr-2 tabular-nums">
                {t('orders.pagination.rows', { defaultValue: 'Rows' })} {pageSize} · {t('orders.pagination.page', { defaultValue: 'Page' }).toLowerCase()} {totalPages === 0 ? 0 : currentPage + 1} {t('orders.pagination.of', { defaultValue: 'of' })} {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                disabled={currentPage === 0}
                className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label={t('orders.pagination.previous', { defaultValue: 'Previous' })}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                disabled={totalPages === 0 || currentPage >= totalPages - 1}
                className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label={t('orders.pagination.next', { defaultValue: 'Next' })}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          isOpen={showDeleteConfirm}
          onClose={() => { setShowDeleteConfirm(false); setOrderToDelete(null); setSessionToDelete(null); setBulkDelete(false); }}
          onConfirm={handleConfirmDelete}
          title={bulkDelete
            ? t('orders.bulk.deleteTitle', { defaultValue: 'Delete {{n}} orders?', n: selectedRows.length })
            : sessionToDelete
            ? t('orders.deleteModal.sessionTitle', { defaultValue: 'Delete Session' })
            : t('orders.deleteModal.title', { defaultValue: 'Delete Order' })}
          message={bulkDelete
            ? t('orders.bulk.deleteMessage', { defaultValue: 'Hide these {{n}} orders and their chat sessions from order management?', n: selectedRows.length })
            : sessionToDelete
            ? t('orders.messages.deleteSessionConfirmation', { defaultValue: 'Hide this chat session from order management?' })
            : t('orders.messages.deleteConfirmation', { defaultValue: 'Hide this order and its chat session from order management?' })}
          confirmText={t('orders.deleteModal.confirm', { defaultValue: 'Delete' })}
          cancelText={t('orders.deleteModal.cancel', { defaultValue: 'Cancel' })}
          confirmButtonClass="bg-red-500 hover:bg-red-600"
        />

        {/* Order Form Modal */}
        <OrderFormModal
          isOpen={showCreateForm}
          onClose={() => { setShowCreateForm(false); }}
          onSubmit={handleOrderFormSubmit}
          order={null}
          isSubmitting={isSubmitting}
        />
        {/* Lead Conversation Slide-over */}
        {viewingLead && (
          <div className="fixed inset-0 z-50 flex justify-end">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => setViewingLead(null)}
            />
            {/* Panel */}
            <div className="relative w-full max-w-lg bg-white shadow-2xl flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-amber-50">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <UserRound className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">
                      {viewingLead.visitor_name || viewingLead.visitor_email || viewingLead.visitor_phone || `Visitor #${viewingLead.guest_uid?.slice(-6) ?? '?'}`}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5 flex-wrap">
                      <Link2 className="w-3 h-3" />
                      <span className="truncate">{viewingLead.invite_label}</span>
                      {viewingLead.visitor_email && (
                        <span className="text-gray-500">{viewingLead.visitor_email}</span>
                      )}
                      {viewingLead.visitor_phone && (
                        <span className="text-gray-500">{viewingLead.visitor_phone}</span>
                      )}
                      {viewingLead.language && (
                        <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded text-xs font-medium">
                          {viewingLead.language.toUpperCase()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setViewingLead(null)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Parts panel header */}
              <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-200 bg-white flex-shrink-0">
                <Package className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-gray-700">
                  {t('orders.leads.panel.parts', { defaultValue: 'Selected Parts' })}
                </span>
                {viewingLead.parts_count > 0 && (
                  <span className="ml-1 bg-green-100 text-green-700 text-xs px-1.5 py-0.5 rounded-full font-semibold">
                    {viewingLead.parts_count}
                  </span>
                )}
              </div>

              {/* Parts list */}
              {(() => {
                const parts: SelectedPart[] = viewingLead.selected_parts || [];
                return (
                  <div className="flex-1 overflow-y-auto bg-gray-50">
                    {parts.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full py-20 text-gray-400">
                        <Package className="w-10 h-10 mb-3 text-gray-300" />
                        <p className="text-sm font-medium">{t('orders.leads.noParts', { defaultValue: 'No parts found yet' })}</p>
                        <p className="text-xs text-gray-400 mt-1">{t('orders.leads.noPartsHint', { defaultValue: 'Parts will appear here as the visitor searches' })}</p>
                      </div>
                    ) : (
                      <div className="p-4 space-y-3">
                        {parts.map((part, idx) => {
                          const pd = part.structured_data?.part_data || part.structured_data?.PART_DATA || {};
                          const partNumber = pd.part_number || pd.PART_ID || part.partNumber || part.partId || '—';
                          const description = pd.description || pd.DESCRIPTION || part.description || part.category || '—';
                          const price = pd.price || pd.PRICE || (part.pricing ? String(part.pricing) : null);
                          const query = part.related_user_query;
                          const score = part.probability_score;

                          return (
                            <div key={idx} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <div className="w-8 h-8 rounded-lg bg-green-50 border border-green-200 flex items-center justify-center flex-shrink-0">
                                    <Tag className="w-4 h-4 text-green-600" />
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-mono text-xs font-semibold text-gray-800 truncate">{partNumber}</p>
                                    {part.category && (
                                      <p className="text-xs text-gray-400 truncate">{part.category}</p>
                                    )}
                                  </div>
                                </div>
                                {price && price !== 'undefined' && price !== '--' && (
                                  <span className="text-sm font-semibold text-gray-900 flex-shrink-0">{price}</span>
                                )}
                              </div>

                              {description && description !== '—' && (
                                <p className="mt-2 text-sm text-gray-700 leading-snug">{description}</p>
                              )}

                              <div className="mt-2 flex flex-wrap gap-1.5">
                                {query && (
                                  <span className="inline-flex items-center gap-1 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">
                                    <Search className="w-3 h-3" />
                                    {query}
                                  </span>
                                )}
                                {score !== undefined && (
                                  <span className="text-xs text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">
                                    {Math.round(score * 100)}% match
                                  </span>
                                )}
                              </div>

                              {part.complementary_parts && part.complementary_parts.length > 0 && (
                                <div className="mt-2 pt-2 border-t border-gray-100">
                                  <p className="text-xs text-gray-400 mb-1">{t('orders.leads.complementaryParts', { defaultValue: 'Also recommended' })}:</p>
                                  <div className="flex flex-wrap gap-1">
                                    {part.complementary_parts.map((cp, ci) => (
                                      <span key={ci} className="text-xs bg-gray-100 text-gray-600 rounded px-1.5 py-0.5">
                                        {cp.part_number || cp.description}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Footer meta */}
              <div className="px-5 py-3 border-t border-gray-200 bg-white flex items-center justify-between text-xs text-gray-500 flex-shrink-0">
                <span className="text-green-600 font-medium">
                  {viewingLead.parts_count} {t('orders.leads.partsFound', { defaultValue: 'parts found' })}
                </span>
                <span>{new Date(viewingLead.created_at).toLocaleString()}</span>
              </div>

              {/* Convert to Order CTA */}
              <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex-shrink-0">
                <button
                  onClick={(e) => handleConvertLeadToOrder(viewingLead, e)}
                  disabled={convertingLead === viewingLead.id}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 shadow-sm"
                >
                  {convertingLead === viewingLead.id
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <ArrowRightCircle className="w-4 h-4" />}
                  {t('orders.leads.convertToOrder', { defaultValue: 'Convert to Order' })}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </AppLayout>
  );
};

export default OrdersManagement;
